/**
 * Coffre deverrouille : compose `@klavox/vault-model` (structure, revisions,
 * tombstones) et `@klavox/crypto-core` (chiffrement) au-dessus d'un
 * `VaultRepository`. C'est le SEUL endroit de l'app ou `secrets` circule en
 * clair -- toujours borne a l'appel courant, jamais persiste tel quel
 * (`docs/ARCHITECTURE.md` §2, §5).
 */
import {
  base64ToBytes,
  bytesToBase64,
  decryptJson,
  enableRecoveryKey,
  encryptJson,
  protectVaultKeyWithPasskey,
  sealEmergencyAccess,
  type AeadAlgorithmId,
  type EnabledRecoveryKey,
  type PasskeyProtectedSymmetricKey,
  type EntrySecrets,
  type SecretKey,
} from '@klavox/crypto-core';
import {
  MAX_ATTACHMENT_BYTES,
  MAX_ATTACHMENT_FILE_NAME_LENGTH,
  MAX_VAULT_ATTACHMENTS_STORAGE,
  authenticateVaultRecords,
  createVaultAttachment,
  decryptVaultAttachment,
  encryptVaultAttachment,
  markVaultAttachmentDeleted,
  createVaultFolder,
  createVaultItem,
  decryptVaultFolder,
  decryptVaultItem,
  encryptVaultFolder,
  encryptVaultItem,
  markVaultFolderDeleted,
  markVaultItemDeleted,
  moveVaultFolder,
  moveVaultItem,
  renameVaultFolder,
  setVaultFolderTravelSafe,
  setVaultItemFavorite,
  updateVaultItemSecrets,
  type AuthenticatedVaultRecords,
  type EncryptedVaultAttachment,
  type EncryptedVaultFolder,
  type EncryptedVaultItem,
  type VaultAttachmentMetadata,
  type VaultFolder,
  type VaultItem,
  type VaultItemType,
} from '@klavox/vault-model';
import type { VaultRepository } from './repository.js';
import { armPasswordRenewal, settlePasswordExpiry } from './password-expiry.js';
import { normalizePasswordHistory, readPasswordHistory, withPasswordHistory } from './password-history.js';
import { withItemHistory } from './item-history.js';
import { TYPED_FIELD_GROUPS, fieldSpecs, readTypedFields } from './typed-fields.js';
import { planTravelPurge, type TravelPurgePlan } from './travel-mode.js';
import { matchesArchiveFilter, withArchived, type ArchiveFilter } from './entry-archive.js';
import { withHealthExcluded, withIgnoredHealthAlert, type IgnorableHealthAlert } from './health-exclusion.js';
import { withPasskeyElsewhere } from './passkey-elsewhere.js';
import { GENERATOR_HISTORY_AAD, type GeneratorHistoryEntry } from './generator-history.js';
import { isTrashExpired, isTrashed, matchesTrashFilter, withTrashed, type TrashFilter } from './entry-trash.js';
import { mergeDuplicateSecrets } from './entry-merge.js';
import { cloneSecrets } from './entry-clone.js';
import { linkCandidates, readLinks, relatedEntries, withLink, withMergedLinks, withoutLink, withRetargetedLinks } from './entry-links.js';
import { ENTRY_SORT_DEFAULT, compareEntries, type EntrySortOrder } from './entry-sort.js';
import { bulkApplies, type BulkAction } from './entry-bulk.js';
import { folderSubtreeIds } from './folder-tree.js';
import {
  isAdvancedSearch,
  matchesAdvancedSearch,
  matchesSearchTerms,
  parseAdvancedSearch,
  searchTerms,
  type SearchableTexts,
} from './entry-search.js';
import { isCheckboxField, readCustomFields } from './custom-fields.js';
import { isSecretLabel } from './csv-import.js';
import { addTag, collectTags, hasTagWithin, normalizeTag, readTags, removeTag, removeTagWithin, renameTag, tagsChanged, type TagCount } from './entry-tags.js';

export interface CreateEntryInput {
  readonly type: VaultItemType;
  readonly secrets: EntrySecrets;
  readonly folderId?: string | null;
  readonly favorite?: boolean;
}

function matchesQuery(secrets: EntrySecrets, query: string): boolean {
  if (isAdvancedSearch(query)) return matchesAdvancedSearch(searchableTexts(secrets), parseAdvancedSearch(query));
  const terms = searchTerms(query);
  if (terms.length === 0) return true;
  const haystacks = Object.values(searchableTexts(secrets)).flat();
  return matchesSearchTerms(haystacks, terms);
}

/** Textes cherches d'une entree, ranges par champ pour la recherche avancee (`>nom:banque`) ; jamais un champ sensible. */
function searchableTexts(secrets: EntrySecrets): SearchableTexts {
  const fieldTexts: string[] = [];
  // Champs types non sensibles (titulaire, banque, nom, ville, SSID...) : jamais numero de carte, CVV ni pieces.
  for (const group of TYPED_FIELD_GROUPS) {
    const fields = readTypedFields(secrets, group);
    // Une liste fermee (securite Wi-Fi, reseau masque) range un code, pas un mot que l'on chercherait.
    fieldTexts.push(...fieldSpecs(group).filter((spec) => !spec.sensitive && spec.options === undefined && fields[spec.key] !== undefined).map((spec) => fields[spec.key]!));
  }
  fieldTexts.push(...searchableCustomFieldTexts(secrets));
  return {
    name: typeof secrets.name === 'string' ? [secrets.name] : [],
    username: typeof secrets.username === 'string' ? [secrets.username] : [],
    notes: typeof secrets.notes === 'string' ? [secrets.notes] : [],
    uri: Array.isArray(secrets.uris) ? secrets.uris.filter((u): u is string => typeof u === 'string') : [],
    tag: readTags(secrets),
    field: fieldTexts,
  };
}

/**
 * Champs personnalises cherches, comme l'index de Bitwarden (`fieldExtractor` : nom de chaque champ, valeur des seuls
 * champs texte) : le NOM de chaque champ, la VALEUR des seuls champs visibles. Jamais la valeur d'un champ masque ni
 * celle d'un champ dont le libelle designe un secret (« PIN », « Numero de compte ») meme laisse visible : taper ses
 * chiffres ne doit pas faire apparaitre l'entree. Ni l'etat d'une case a cocher : « true » n'est pas un mot cherche.
 */
export function searchableCustomFieldTexts(secrets: EntrySecrets): string[] {
  return readCustomFields(secrets).flatMap((field) =>
    field.hidden === true || isCheckboxField(field) || isSecretLabel(field.name) ? [field.name] : [field.name, field.value],
  );
}

/** Piece jointe telle qu'affichee : nom et taille dechiffres, jamais le contenu. */
export interface AttachmentSummary {
  readonly id: string;
  readonly itemId: string;
  readonly fileName: string;
  readonly mimeType?: string;
  /** Taille du fichier en clair (octets). */
  readonly size: number;
  readonly createdAt: number;
}

export interface AttachmentFile {
  readonly fileName: string;
  readonly mimeType?: string;
  readonly bytes: Uint8Array;
}

/** Fichier refuse (vide, trop volumineux, quota du coffre atteint) : message affichable tel quel. */
export class AttachmentLimitError extends Error {
  override readonly name = 'AttachmentLimitError';
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  const units = ['Ko', 'Mo', 'Go'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toLocaleString('fr-FR', { maximumFractionDigits: value < 10 ? 1 : 0 })} ${units[unit]}`;
}

/** Controle AVANT lecture du fichier : evite de charger en memoire un fichier qui sera refuse. */
export function assertAttachmentSize(size: number): void {
  if (size === 0) throw new AttachmentLimitError('fichier vide');
  if (size > MAX_ATTACHMENT_BYTES) {
    throw new AttachmentLimitError(`fichier trop volumineux (${formatFileSize(MAX_ATTACHMENT_BYTES)} maximum)`);
  }
}

/** Nom borne a la limite du modele, extension conservee. */
function boundedFileName(name: string): string {
  const trimmed = name.trim() || 'fichier';
  if (trimmed.length <= MAX_ATTACHMENT_FILE_NAME_LENGTH) return trimmed;
  const extension = /\.[^.]{1,16}$/u.exec(trimmed)?.[0] ?? '';
  return trimmed.slice(0, MAX_ATTACHMENT_FILE_NAME_LENGTH - extension.length) + extension;
}

function decodedBase64Length(value: string): number {
  const padding = value.endsWith('==') ? 2 : value.endsWith('=') ? 1 : 0;
  return Math.floor((value.length * 3) / 4) - padding;
}

const isActive = (record: { readonly deletedAt: number | null }): boolean => record.deletedAt === null;

/** Coffre en memoire, dechiffre a la demande. Une instance par session deverrouillee. */
export class VaultStore {
  readonly #repository: VaultRepository;
  readonly #vaultKey: SecretKey;
  /** Resumes dechiffres par version de piece jointe : evite de redechiffrer un blob de 10 Mo a chaque affichage. */
  readonly #attachmentSummaries = new Map<string, AttachmentSummary>();

  constructor(repository: VaultRepository, vaultKey: SecretKey) {
    this.#repository = repository;
    this.#vaultKey = vaultKey;
  }

  /** Efface la `vaultKey` en memoire (verrouillage). Le store est inutilisable ensuite. */
  lock(): void {
    this.#attachmentSummaries.clear();
    this.#vaultKey.destroy();
  }

  /**
   * Genere une nouvelle cle de recuperation et y scelle la `vaultKey` du
   * coffre deverrouille (`docs/ARCHITECTURE.md` §4). L'appelant DOIT afficher
   * `recoveryKeyDisplay` a l'utilisateur puis appeler `recoveryKey.destroy()`
   * une fois confirme -- ce store ne la conserve jamais.
   */
  createRecoveryKey(algorithm?: AeadAlgorithmId): EnabledRecoveryKey {
    return enableRecoveryKey(this.#vaultKey, algorithm);
  }

  /**
   * Scelle la `vaultKey` sous la sortie PRF d'une passkey (R12,
   * `docs/ARCHITECTURE.md` §4) : un RACCOURCI de deverrouillage local, jamais
   * une racine. Meme forme que `createRecoveryKey` -- la `vaultKey` reste
   * encapsulee ici, l'appelant ne recoit qu'un blob opaque a persister.
   */
  sealForPasskey(prfOutput: SecretKey, algorithm?: AeadAlgorithmId): PasskeyProtectedSymmetricKey {
    return protectVaultKeyWithPasskey({ vaultKey: this.#vaultKey, prfOutput, ...(algorithm !== undefined ? { algorithm } : {}) });
  }

  /**
   * Fiche d'un envoi Klavox Send (R15) scellee sous la `vaultKey` : libelle et
   * secret d'URL, soit ce qui permet a l'EMETTEUR de retrouver son propre lien.
   * Meme forme que `createRecoveryKey`/`sealForPasskey` -- la cle reste ici,
   * l'appelant ne manipule qu'un blob opaque (`send-client.ts`).
   */
  sealSendReceipt(sendId: string, receipt: unknown): string {
    return encryptJson({ entry: receipt, vaultKey: this.#vaultKey, entryId: sendId });
  }

  /** Ouvre une fiche d'envoi. Leve si le blob vient d'un autre coffre ou a ete altere. */
  openSendReceipt<T>(sendId: string, blob: string): T {
    return decryptJson<T>({ ciphertext: blob, vaultKey: this.#vaultKey, entryId: sendId });
  }

  /**
   * Historique du generateur (`generator-history.ts`), scelle sous la `vaultKey`
   * avec sa propre donnee associee : il n'en sort qu'un blob opaque, range sur
   * cet appareil seulement.
   */
  sealGeneratorHistory(history: readonly GeneratorHistoryEntry[]): string {
    return encryptJson({ entry: history, vaultKey: this.#vaultKey, entryId: GENERATOR_HISTORY_AAD });
  }

  /** Ouvre l'historique du generateur. Leve si le blob vient d'un autre coffre ou a ete altere. */
  openGeneratorHistory(blob: string): unknown {
    return decryptJson<unknown>({ ciphertext: blob, vaultKey: this.#vaultKey, entryId: GENERATOR_HISTORY_AAD });
  }

  /**
   * Scelle la `vaultKey` sous le KIT d'un contact de confiance (R32,
   * `docs/EMERGENCY-ACCESS-DESIGN.md`). Meme forme que `createRecoveryKey` :
   * la cle du coffre reste ici, l'appelant ne recoit qu'un blob opaque. Le kit
   * lui est fourni par `lib/emergency-access.ts` et n'est jamais persiste.
   */
  sealForEmergencyKit(grantId: string, kit: SecretKey): string {
    return sealEmergencyAccess({ vaultKey: this.#vaultKey, kit, grantId });
  }

  /** Libelle d'un contact de confiance, scelle sous la `vaultKey` : le serveur ne sait pas qui est l'heritier. */
  sealEmergencyLabel(grantId: string, label: unknown): string {
    return encryptJson({ entry: label, vaultKey: this.#vaultKey, entryId: grantId });
  }

  /** Ouvre un libelle de contact. Leve si le blob vient d'un autre coffre ou a ete altere. */
  openEmergencyLabel<T>(grantId: string, blob: string): T {
    return decryptJson<T>({ ciphertext: blob, vaultKey: this.#vaultKey, entryId: grantId });
  }

  /**
   * Ne retient d'un lot venu de HORS de l'appareil (serveur, fichier d'export)
   * que les enregistrements authentiques sous la `vaultKey` (contenu ET
   * metadonnees). A fusionner ensuite avec l'etat LOCAL, jamais a substituer.
   */
  authenticateExternal(batch: {
    readonly items?: readonly EncryptedVaultItem[];
    readonly folders?: readonly EncryptedVaultFolder[];
    readonly attachments?: readonly EncryptedVaultAttachment[];
  }): AuthenticatedVaultRecords {
    return authenticateVaultRecords({
      items: batch.items,
      folders: batch.folders,
      attachments: batch.attachments,
      vaultKey: this.#vaultKey,
    });
  }

  /**
   * Reecrit en format v2 (metadonnees authentifiees) les enregistrements
   * locaux encore en format v1. La revision progresse pour que la version
   * migree se propage a la prochaine synchro. A appeler au deverrouillage.
   *
   * @returns nombre d'enregistrements migres
   */
  async upgradeLegacyRecords(): Promise<number> {
    const vaultKey = this.#vaultKey;
    const now = Date.now();
    const bump = <T extends { readonly revision: number; readonly updatedAt: number }>(record: T): T => ({
      ...record,
      revision: record.revision + 1,
      updatedAt: Math.max(now, record.updatedAt + 1),
    });
    const items = await this.#repository.listItems();
    const folders = await this.#repository.listFolders();
    const authentic = authenticateVaultRecords({ items, folders, vaultKey });
    const legacyItems = items
      .filter((item) => !authentic.items.includes(item))
      .map((item) => encryptVaultItem({ item: bump(decryptVaultItem({ item, vaultKey, acceptLegacyFormat: true })), vaultKey }));
    const legacyFolders = folders
      .filter((folder) => !authentic.folders.includes(folder))
      .map((folder) =>
        encryptVaultFolder({ folder: bump(decryptVaultFolder({ folder, vaultKey, acceptLegacyFormat: true })), vaultKey }),
      );
    if (legacyItems.length > 0) await this.#repository.putItems(legacyItems);
    if (legacyFolders.length > 0) await this.#repository.putFolders(legacyFolders);
    return legacyItems.length + legacyFolders.length;
  }

  async #decryptAllItems(): Promise<VaultItem[]> {
    const encrypted = await this.#repository.listItems();
    return encrypted.map((item) => decryptVaultItem({ item, vaultKey: this.#vaultKey }));
  }

  /**
   * Entrees actives (non supprimees), triees par nom. Les archivees
   * (`entry-archive.ts`) en sont exclues sauf `archived: 'only' | 'include'` :
   * tout appelant qui doit voir le coffre ENTIER (export, sante) le demande.
   * La corbeille (`entry-trash.ts`) est exclue de TOUTES les vues, archives
   * comprises, sauf `trash: 'only' | 'include'` : une entree jetee ne compte
   * plus nulle part tant qu'on ne l'a pas restauree.
   * Ordre : `sort` (`entry-sort.ts`), nom A-Z par defaut.
   */
  async listEntries(
    options: {
      readonly folderId?: string | null;
      readonly favoritesOnly?: boolean;
      /** Vue d'une etiquette (`entry-tags.ts`), sous-etiquettes comprises, sans tenir compte de la casse. */
      readonly tag?: string;
      readonly archived?: ArchiveFilter;
      readonly trash?: TrashFilter;
      readonly sort?: EntrySortOrder;
    } = {},
  ): Promise<VaultItem[]> {
    const items = (await this.#decryptAllItems()).filter((item) => item.deletedAt === null);
    const filtered = items.filter((item) => {
      if (!matchesTrashFilter(item.secrets, options.trash ?? 'exclude')) return false;
      // La vue Corbeille montre aussi les entrees qui etaient archivees avant d'etre jetees.
      if (options.trash !== 'only' && !matchesArchiveFilter(item.secrets, options.archived ?? 'exclude')) return false;
      if (options.folderId !== undefined && item.folderId !== options.folderId) return false;
      if (options.favoritesOnly === true && !item.favorite) return false;
      if (options.tag !== undefined && !hasTagWithin(item.secrets, options.tag)) return false;
      return true;
    });
    return filtered.sort(compareEntries(options.sort ?? ENTRY_SORT_DEFAULT));
  }

  /**
   * Recherche hors archives et hors corbeille par defaut, comme chez Bitwarden ; la vue Archives cherche dans les
   * archives (`archived: 'only'`), la vue Corbeille dans la corbeille (`trash: 'only'`).
   */
  async searchEntries(
    query: string,
    options: { readonly archived?: ArchiveFilter; readonly trash?: TrashFilter; readonly sort?: EntrySortOrder } = {},
  ): Promise<VaultItem[]> {
    const items = (await this.#decryptAllItems()).filter((item) => item.deletedAt === null);
    return items
      .filter(
        (item) =>
          matchesTrashFilter(item.secrets, options.trash ?? 'exclude') &&
          (options.trash === 'only' || matchesArchiveFilter(item.secrets, options.archived ?? 'exclude')) &&
          matchesQuery(item.secrets, query),
      )
      .sort(compareEntries(options.sort ?? ENTRY_SORT_DEFAULT));
  }

  /**
   * Etiquettes de la barre laterale : celles des entrees visibles dans « Tous les elements » (ni archivees, ni a la
   * corbeille), comptees -- une etiquette listee mene toujours a au moins une entree.
   */
  async listTags(): Promise<TagCount[]> {
    return collectTags(await this.listEntries());
  }

  async getEntry(id: string): Promise<VaultItem | null> {
    const items = await this.#repository.listItems();
    const found = items.find((item) => item.id === id && item.deletedAt === null);
    if (found === undefined) return null;
    return decryptVaultItem({ item: found, vaultKey: this.#vaultKey });
  }

  async createEntry(input: CreateEntryInput): Promise<VaultItem> {
    const history = normalizePasswordHistory(readPasswordHistory(input.secrets), input.secrets.password ?? '');
    const { passwordHistory: _history, ...given } = input.secrets;
    // Une regle de renouvellement choisie a la creation arme aussitot son echeance (`password-expiry.ts`).
    const secrets = armPasswordRenewal(given, Date.now());
    const item = createVaultItem({
      type: input.type,
      secrets: history.length > 0 ? { ...secrets, passwordHistory: history } : secrets,
      folderId: input.folderId ?? null,
      favorite: input.favorite ?? false,
    });
    await this.#repository.putItem(encryptVaultItem({ item, vaultKey: this.#vaultKey }));
    return item;
  }

  /**
   * Remplace les secrets de l'entree. Le mot de passe remplace rejoint
   * l'historique chiffre (`password-history.ts`), repris de la version
   * precedente sauf si `secrets.passwordHistory` est fourni (ex. `[]` pour l'effacer). Le contenu
   * remplace rejoint de meme les versions chiffrees de l'entree (`item-history.ts`). Un changement du mot de passe
   * acquitte son echeance passee ou proche, ou la reporte selon la regle de renouvellement (`password-expiry.ts`).
   */
  async updateEntrySecrets(id: string, secrets: EntrySecrets): Promise<VaultItem> {
    const current = await this.#requireEncryptedItem(id);
    const decrypted = decryptVaultItem({ item: current, vaultKey: this.#vaultKey });
    const now = Date.now();
    const next = withItemHistory(decrypted.secrets, settlePasswordExpiry(decrypted.secrets, withPasswordHistory(decrypted.secrets, secrets, now), now), now);
    const updated = updateVaultItemSecrets(decrypted, next);
    await this.#repository.putItem(encryptVaultItem({ item: updated, vaultKey: this.#vaultKey }));
    return updated;
  }

  async moveEntry(id: string, folderId: string | null): Promise<VaultItem> {
    const current = await this.#requireEncryptedItem(id);
    const decrypted = decryptVaultItem({ item: current, vaultKey: this.#vaultKey });
    const moved = moveVaultItem(decrypted, folderId);
    await this.#repository.putItem(encryptVaultItem({ item: moved, vaultKey: this.#vaultKey }));
    return moved;
  }

  async setFavorite(id: string, favorite: boolean): Promise<VaultItem> {
    const current = await this.#requireEncryptedItem(id);
    const decrypted = decryptVaultItem({ item: current, vaultKey: this.#vaultKey });
    const next = setVaultItemFavorite(decrypted, favorite);
    await this.#repository.putItem(encryptVaultItem({ item: next, vaultKey: this.#vaultKey }));
    return next;
  }

  /**
   * Archive (ou desarchive) l'entree : seul le drapeau scelle change, ni le
   * mot de passe ni son historique (`entry-archive.ts`).
   */
  async setArchived(id: string, archived: boolean): Promise<VaultItem> {
    const current = await this.#requireEncryptedItem(id);
    const decrypted = decryptVaultItem({ item: current, vaultKey: this.#vaultKey });
    const next = updateVaultItemSecrets(decrypted, withArchived(decrypted.secrets, archived));
    await this.#repository.putItem(encryptVaultItem({ item: next, vaultKey: this.#vaultKey }));
    return next;
  }

  /**
   * Elements lies a l'entree (`entry-links.ts`), dans les deux sens, archives comprises, et ceux que l'on peut encore
   * lui lier. Une entree a la corbeille n'en a aucun.
   */
  async relatedEntries(id: string): Promise<{ readonly related: VaultItem[]; readonly candidates: VaultItem[] }> {
    const all = (await this.#decryptAllItems()).filter((item) => item.deletedAt === null);
    const entry = all.find((item) => item.id === id);
    if (entry === undefined || isTrashed(entry.secrets)) return { related: [], candidates: [] };
    return { related: relatedEntries(entry, all), candidates: linkCandidates(entry, all) };
  }

  /**
   * Lie `id` a `otherId` : seul le lien scelle dans `id` change, sans version ni revision de `otherId` (le lien se lit
   * dans les deux sens). Refuse (sans rien ecrire) l'entree elle-meme, une entree absente ou a la corbeille.
   */
  async linkEntries(id: string, otherId: string): Promise<VaultItem> {
    if (id === otherId) throw new Error(`une entree ne se lie pas a elle-meme: ${id}`);
    const current = decryptVaultItem({ item: await this.#requireEncryptedItem(id), vaultKey: this.#vaultKey });
    const other = decryptVaultItem({ item: await this.#requireEncryptedItem(otherId), vaultKey: this.#vaultKey });
    for (const item of [current, other]) {
      if (item.deletedAt !== null || isTrashed(item.secrets)) throw new Error(`entree a la corbeille: ${item.id}`);
    }
    const next = updateVaultItemSecrets(current, withLink(current.secrets, otherId));
    await this.#repository.putItem(encryptVaultItem({ item: next, vaultKey: this.#vaultKey }));
    return next;
  }

  /** Defait le lien entre `id` et `otherId`, quel que soit le cote qui le portait ; n'ecrit que l'entree qui le portait. */
  async unlinkEntries(id: string, otherId: string): Promise<void> {
    for (const [from, to] of [[id, otherId], [otherId, id]] as const) {
      const found = (await this.#repository.listItems()).find((item) => item.id === from);
      if (found === undefined) continue;
      const decrypted = decryptVaultItem({ item: found, vaultKey: this.#vaultKey });
      if (!readLinks(decrypted.secrets).includes(to)) continue;
      const next = updateVaultItemSecrets(decrypted, withoutLink(decrypted.secrets, to));
      await this.#repository.putItem(encryptVaultItem({ item: next, vaultKey: this.#vaultKey }));
    }
  }

  /**
   * Exclut (ou reinclut) l'entree de la Sante du coffre : seul le drapeau
   * scelle change (`health-exclusion.ts`).
   */
  async setHealthExcluded(id: string, excluded: boolean): Promise<VaultItem> {
    const current = await this.#requireEncryptedItem(id);
    const decrypted = decryptVaultItem({ item: current, vaultKey: this.#vaultKey });
    const next = updateVaultItemSecrets(decrypted, withHealthExcluded(decrypted.secrets, excluded));
    await this.#repository.putItem(encryptVaultItem({ item: next, vaultKey: this.#vaultKey }));
    return next;
  }

  /**
   * Ignore (ou signale de nouveau) une alerte de la Sante du coffre pour cette entree : seul le drapeau scelle change
   * (`health-exclusion.ts`).
   */
  async setHealthAlertIgnored(id: string, alert: IgnorableHealthAlert, ignored: boolean): Promise<VaultItem> {
    const current = await this.#requireEncryptedItem(id);
    const decrypted = decryptVaultItem({ item: current, vaultKey: this.#vaultKey });
    const next = updateVaultItemSecrets(decrypted, withIgnoredHealthAlert(decrypted.secrets, alert, ignored));
    await this.#repository.putItem(encryptVaultItem({ item: next, vaultKey: this.#vaultKey }));
    return next;
  }

  /**
   * Declare (ou retire) la passkey de l'entree comme rangee ailleurs (`passkey-elsewhere.ts`) : la Sante du coffre
   * cesse (ou recommence) de proposer d'en creer une. Le drapeau est re-chiffre avec les secrets.
   */
  async setPasskeyElsewhere(id: string, elsewhere: boolean): Promise<VaultItem> {
    const current = await this.#requireEncryptedItem(id);
    const decrypted = decryptVaultItem({ item: current, vaultKey: this.#vaultKey });
    const next = updateVaultItemSecrets(decrypted, withPasskeyElsewhere(decrypted.secrets, elsewhere));
    await this.#repository.putItem(encryptVaultItem({ item: next, vaultKey: this.#vaultKey }));
    return next;
  }

  /**
   * « Dupliquer » : nouvelle entree, nouvel identifiant, meme type et meme
   * dossier, secrets repris par `cloneSecrets` (ni passkeys, ni historique, ni
   * archive ; re-demande du mot de passe maitre conservee). Les pieces jointes
   * restent a l'original : les recopier doublerait leur poids dans le quota.
   * Refuse une entree a la corbeille ou supprimee : on restaure d'abord.
   */
  async duplicateEntry(id: string): Promise<VaultItem> {
    const current = await this.#requireEncryptedItem(id);
    const decrypted = decryptVaultItem({ item: current, vaultKey: this.#vaultKey });
    if (decrypted.deletedAt !== null || isTrashed(decrypted.secrets)) throw new Error(`entree a la corbeille: ${id}`);
    return this.createEntry({ type: decrypted.type, secrets: cloneSecrets(decrypted.secrets), folderId: decrypted.folderId });
  }

  /**
   * « Fusionner » des entrees en double (Sante du coffre) : `keepId` recoit ce que portaient les copies
   * (`mergeDuplicateSecrets` : adresses, notes, champs, passkeys, historique, valeurs divergentes en champs masques),
   * leurs pieces jointes (re-chiffrees, nonce neuf, sous l'entree gardee AVANT d'etre retirees de la copie), le
   * favori et les elements lies (`withMergedLinks` ; une entree qui designait une copie designe la gardee) ; puis chaque copie part a la corbeille, restaurable. Refuse (sans rien ecrire) une entree absente, a la
   * corbeille, d'un autre type, ou une fusion qui perdrait un champ personnalise.
   */
  async mergeEntries(keepId: string, otherIds: readonly string[], now: number = Date.now()): Promise<VaultItem> {
    const ids = otherIds.filter((id, index) => id !== keepId && otherIds.indexOf(id) === index);
    if (ids.length === 0) throw new Error('fusion: aucune autre copie');
    const [keep, ...others] = await Promise.all(
      [keepId, ...ids].map(async (id) => decryptVaultItem({ item: await this.#requireEncryptedItem(id), vaultKey: this.#vaultKey })),
    );
    for (const item of [keep!, ...others]) {
      if (item.deletedAt !== null || isTrashed(item.secrets)) throw new Error(`entree a la corbeille: ${item.id}`);
      if (item.type !== keep!.type) throw new Error(`fusion: types differents (${item.id})`);
    }
    const merged = mergeDuplicateSecrets(
      keep!.secrets,
      others.map((item) => item.secrets),
    );
    if (merged === null) throw new Error('fusion: trop de champs personnalises');
    let result = await this.updateEntrySecrets(keep!.id, withMergedLinks(merged, keep!.id, others));
    if (!keep!.favorite && others.some((item) => item.favorite)) result = await this.setFavorite(keep!.id, true);
    if (keep!.folderId === null) {
      const folderId = others.find((item) => item.folderId !== null)?.folderId ?? null;
      if (folderId !== null) result = await this.moveEntry(keep!.id, folderId);
    }
    // Les entrees qui designaient une copie designent desormais la copie gardee (lien seul, sans version).
    const droppedIds = new Set(others.map((item) => item.id));
    for (const item of await this.#decryptAllItems()) {
      if (item.deletedAt !== null || item.id === keep!.id || droppedIds.has(item.id)) continue;
      const retargeted = withRetargetedLinks(item.secrets, droppedIds, keep!.id);
      if (retargeted === item.secrets) continue;
      await this.#repository.putItem(encryptVaultItem({ item: updateVaultItemSecrets(item, retargeted), vaultKey: this.#vaultKey }));
    }
    for (const other of others) {
      for (const summary of await this.listAttachments(other.id)) {
        await this.addAttachment(keep!.id, await this.readAttachment(other.id, summary.id));
        await this.deleteAttachment(other.id, summary.id);
      }
      await this.trashEntry(other.id, now);
    }
    return result;
  }

  /**
   * « Supprimer » : l'entree part a la corbeille (`entry-trash.ts`), restaurable
   * telle quelle -- pieces jointes, historique, dossier et archive compris. Seul
   * le drapeau scelle change.
   */
  async trashEntry(id: string, now: number = Date.now()): Promise<VaultItem> {
    return this.#setTrashed(id, now);
  }

  /** Sort l'entree de la corbeille, a sa place d'origine. */
  async restoreEntry(id: string): Promise<VaultItem> {
    return this.#setTrashed(id, null);
  }

  async #setTrashed(id: string, at: number | null): Promise<VaultItem> {
    const current = await this.#requireEncryptedItem(id);
    const decrypted = decryptVaultItem({ item: current, vaultKey: this.#vaultKey });
    const next = updateVaultItemSecrets(decrypted, withTrashed(decrypted.secrets, at));
    await this.#repository.putItem(encryptVaultItem({ item: next, vaultKey: this.#vaultKey }));
    return next;
  }

  /**
   * Selection multiple (`entry-bulk.ts`) : applique `action` aux entrees `ids`
   * qu'elle concerne (`bulkApplies`), en UNE ecriture -- meme transformation
   * que le geste unitaire (favori, dossier, drapeau scelle). « Supprimer
   * definitivement » passe par `deleteEntry`, pieces jointes comprises.
   * Refuse un dossier inconnu plutot que de ranger dans le vide.
   * @returns nombre d'entrees reellement modifiees
   */
  async applyBulk(
    ids: readonly string[],
    action: BulkAction,
    options: { readonly folderId?: string | null; readonly tag?: string; readonly now?: number } = {},
  ): Promise<number> {
    const folderId = options.folderId ?? null;
    const tag = options.tag ?? '';
    if (action === 'move' && folderId !== null && !(await this.listFolders()).some((folder) => folder.id === folderId)) {
      throw new Error(`dossier introuvable: ${folderId}`);
    }
    if ((action === 'tag' || action === 'untag') && normalizeTag(tag).length === 0) throw new Error('etiquette vide');
    const wanted = new Set(ids);
    const targets = (await this.#decryptAllItems()).filter((item) => wanted.has(item.id) && bulkApplies(item, action, folderId, tag));
    if (action === 'purge') {
      for (const item of targets) await this.deleteEntry(item.id);
      return targets.length;
    }
    const now = options.now ?? Date.now();
    const next = targets.map((item) => {
      switch (action) {
        case 'favorite':
        case 'unfavorite':
          return setVaultItemFavorite(item, action === 'favorite');
        case 'move':
          return moveVaultItem(item, folderId);
        case 'tag':
          return updateVaultItemSecrets(item, addTag(item.secrets, tag));
        case 'untag':
          return updateVaultItemSecrets(item, removeTag(item.secrets, tag));
        case 'archive':
        case 'unarchive':
          return updateVaultItemSecrets(item, withArchived(item.secrets, action === 'archive'));
        case 'trash':
        case 'restore':
          return updateVaultItemSecrets(item, withTrashed(item.secrets, action === 'trash' ? now : null));
      }
    });
    if (next.length > 0) await this.#repository.putItems(next.map((item) => encryptVaultItem({ item, vaultKey: this.#vaultKey })));
    return next.length;
  }

  /**
   * Renomme une etiquette sur TOUT le coffre (« Rename tag » de 1Password), en
   * une ecriture : archives et corbeille comprises -- sinon une entree
   * restauree ramenerait l'ancien nom. Une entree qui porte deja le nouveau nom
   * fusionne les deux. Les sous-etiquettes suivent. @returns nombre d'entrees reecrites
   */
  async renameTag(from: string, to: string): Promise<number> {
    if (normalizeTag(to).length === 0) throw new Error('etiquette vide');
    return this.#rewriteTags((secrets) => renameTag(secrets, from, to));
  }

  /** Retire une etiquette et ses sous-etiquettes de TOUT le coffre (archives et corbeille comprises) ; les entrees restent. @returns nombre d'entrees reecrites */
  async deleteTag(tag: string): Promise<number> {
    return this.#rewriteTags((secrets) => removeTagWithin(secrets, tag));
  }

  async #rewriteTags(transform: (secrets: EntrySecrets) => EntrySecrets): Promise<number> {
    const next = (await this.#decryptAllItems())
      .filter((item) => item.deletedAt === null)
      .map((item) => ({ item, secrets: transform(item.secrets) }))
      .filter(({ item, secrets }) => tagsChanged(item.secrets, secrets))
      .map(({ item, secrets }) => updateVaultItemSecrets(item, secrets));
    if (next.length > 0) await this.#repository.putItems(next.map((item) => encryptVaultItem({ item, vaultKey: this.#vaultKey })));
    return next.length;
  }

  /** « Vider la corbeille » : suppression definitive de chaque entree jetee. @returns nombre d'entrees supprimees */
  async emptyTrash(): Promise<number> {
    const trashed = await this.listEntries({ trash: 'only' });
    for (const item of trashed) await this.deleteEntry(item.id);
    return trashed.length;
  }

  /**
   * Purge des entrees restees a la corbeille au-dela du delai de conservation
   * (appelee au deverrouillage). @returns nombre d'entrees supprimees
   */
  async purgeExpiredTrash(now: number = Date.now()): Promise<number> {
    const expired = (await this.listEntries({ trash: 'only' })).filter((item) => isTrashExpired(item.secrets, now));
    for (const item of expired) await this.deleteEntry(item.id);
    return expired.length;
  }

  /**
   * Suppression DEFINITIVE (tombstone) : conservee pour la synchro, exclue des listes. Atteinte par « Supprimer
   * definitivement », « Vider la corbeille » et la purge ; « Supprimer » passe par `trashEntry`.
   */
  async deleteEntry(id: string): Promise<void> {
    const current = await this.#requireEncryptedItem(id);
    const decrypted = decryptVaultItem({ item: current, vaultKey: this.#vaultKey });
    const deleted = markVaultItemDeleted(decrypted);
    await this.#repository.putItem(encryptVaultItem({ item: deleted, vaultKey: this.#vaultKey }));
    // Pieces jointes supprimees avec l'entree (tombstones propages a la synchro), jamais orphelines.
    const attachments = (await this.#repository.listAttachments(id)).filter(isActive);
    if (attachments.length > 0) {
      await this.#repository.putAttachments(attachments.map((attachment) => this.#tombstoneAttachment(attachment)));
    }
  }

  // --- Pieces jointes ---------------------------------------------------------

  /** Pieces jointes actives d'une entree, les plus anciennes d'abord. */
  async listAttachments(itemId: string): Promise<AttachmentSummary[]> {
    const encrypted = (await this.#repository.listAttachments(itemId)).filter(isActive);
    return encrypted
      .map((attachment) => {
        const key = `${attachment.id}:${attachment.revision}:${attachment.updatedAt}:${attachment.secrets.length}`;
        let summary = this.#attachmentSummaries.get(key);
        if (summary === undefined) {
          const { secrets } = decryptVaultAttachment({ attachment, vaultKey: this.#vaultKey });
          summary = {
            id: attachment.id,
            itemId: attachment.itemId,
            fileName: secrets.fileName,
            mimeType: secrets.mimeType,
            size: decodedBase64Length(secrets.dataBase64),
            createdAt: attachment.createdAt,
          };
          this.#attachmentSummaries.set(key, summary);
        }
        return summary;
      })
      .sort((a, b) => a.createdAt - b.createdAt);
  }

  /**
   * Chiffre un fichier sous la `vaultKey` et le rattache a une entree active.
   *
   * @throws {AttachmentLimitError} fichier vide, trop volumineux, ou quota du coffre atteint
   */
  async addAttachment(itemId: string, file: AttachmentFile): Promise<AttachmentSummary> {
    const item = await this.#requireEncryptedItem(itemId);
    if (item.deletedAt !== null) throw new Error(`entree supprimee: ${itemId}`);
    assertAttachmentSize(file.bytes.length);
    const attachment = createVaultAttachment({
      itemId,
      fileName: boundedFileName(file.fileName),
      mimeType: file.mimeType === undefined || file.mimeType.length === 0 ? undefined : file.mimeType,
      dataBase64: bytesToBase64(file.bytes),
    });
    const encrypted = encryptVaultAttachment({ attachment, vaultKey: this.#vaultKey });
    // Meme mesure que le serveur (blobs des pieces actives) : un ajout accepte ici passe aussi la synchro.
    const used = (await this.#repository.listAttachments())
      .filter(isActive)
      .reduce((total, current) => total + current.secrets.length, 0);
    if (used + encrypted.secrets.length > MAX_VAULT_ATTACHMENTS_STORAGE) {
      throw new AttachmentLimitError('espace des pieces jointes du coffre plein (environ 100 Mo)');
    }
    await this.#repository.putAttachments([encrypted]);
    return (await this.listAttachments(itemId)).find((summary) => summary.id === attachment.id)!;
  }

  /** Contenu dechiffre d'une piece jointe, a remettre a l'utilisateur puis a oublier. */
  async readAttachment(itemId: string, id: string): Promise<AttachmentFile> {
    const { secrets } = decryptVaultAttachment({ attachment: await this.#requireAttachment(itemId, id), vaultKey: this.#vaultKey });
    return { fileName: secrets.fileName, mimeType: secrets.mimeType, bytes: base64ToBytes(secrets.dataBase64) };
  }

  /** Suppression logique (tombstone), propagee aux autres appareils par la synchro. */
  async deleteAttachment(itemId: string, id: string): Promise<void> {
    const attachment = await this.#requireAttachment(itemId, id);
    await this.#repository.putAttachments([this.#tombstoneAttachment(attachment)]);
  }

  async #requireAttachment(itemId: string, id: string): Promise<EncryptedVaultAttachment> {
    const found = (await this.#repository.listAttachments(itemId)).find((attachment) => attachment.id === id && isActive(attachment));
    if (found === undefined) throw new Error(`piece jointe introuvable: ${id}`);
    return found;
  }

  /** La date de suppression est liee a l'AEAD : re-chiffrement obligatoire (nonce neuf). */
  #tombstoneAttachment(attachment: EncryptedVaultAttachment): EncryptedVaultAttachment {
    const decrypted = decryptVaultAttachment({ attachment, vaultKey: this.#vaultKey });
    return encryptVaultAttachment({ attachment: markVaultAttachmentDeleted(decrypted), vaultKey: this.#vaultKey });
  }

  async #requireEncryptedItem(id: string) {
    const items = await this.#repository.listItems();
    const found = items.find((item) => item.id === id);
    if (found === undefined) throw new Error(`entree introuvable: ${id}`);
    return found;
  }

  // --- Dossiers -------------------------------------------------------------

  async listFolders(): Promise<VaultFolder[]> {
    const encrypted = await this.#repository.listFolders();
    return encrypted
      .filter((folder) => folder.deletedAt === null)
      .map((folder) => decryptVaultFolder({ folder, vaultKey: this.#vaultKey }))
      .sort((a, b) => a.secrets.name.toLowerCase().localeCompare(b.secrets.name.toLowerCase()));
  }

  async createFolder(name: string, parentId: string | null = null): Promise<VaultFolder> {
    const folder = createVaultFolder({ name, parentId });
    await this.#repository.putFolder(encryptVaultFolder({ folder, vaultKey: this.#vaultKey }));
    return folder;
  }

  /** Renomme un dossier (nom chiffre, revision qui avance) ; son drapeau « sur en voyage » est garde. */
  async renameFolder(id: string, name: string): Promise<VaultFolder> {
    const trimmed = name.trim();
    if (trimmed.length === 0) throw new Error('nom de dossier vide');
    const folders = await this.#repository.listFolders();
    const found = folders.find((folder) => folder.id === id && folder.deletedAt === null);
    if (found === undefined) throw new Error(`dossier introuvable: ${id}`);
    const current = decryptVaultFolder({ folder: found, vaultKey: this.#vaultKey });
    const next = renameVaultFolder(current, trimmed);
    if (next !== current) await this.#repository.putFolder(encryptVaultFolder({ folder: next, vaultKey: this.#vaultKey }));
    return next;
  }

  /**
   * Deplace un dossier (et ses sous-dossiers, qui le suivent) sous un autre parent, `null` = racine.
   * Refuse un parent inconnu ou supprime, et le dossier lui-meme ou l'un de ses descendants : le
   * cycle sortirait tout le sous-arbre de l'arborescence (export CXF, barre laterale).
   */
  async moveFolder(id: string, parentId: string | null): Promise<VaultFolder> {
    const folders = (await this.#repository.listFolders()).filter((folder) => folder.deletedAt === null);
    const found = folders.find((folder) => folder.id === id);
    if (found === undefined) throw new Error(`dossier introuvable: ${id}`);
    if (parentId !== null) {
      if (!folders.some((folder) => folder.id === parentId)) throw new Error(`dossier parent introuvable: ${parentId}`);
      if (folderSubtreeIds(folders, id).has(parentId)) throw new Error('un dossier ne peut pas aller sous lui-meme ni sous un de ses sous-dossiers');
    }
    const current = decryptVaultFolder({ folder: found, vaultKey: this.#vaultKey });
    const next = moveVaultFolder(current, parentId);
    if (next !== current) await this.#repository.putFolder(encryptVaultFolder({ folder: next, vaultKey: this.#vaultKey }));
    return next;
  }

  /**
   * Supprime un dossier. Ses entrees passent hors dossier (comme Bitwarden) ;
   * ses sous-dossiers remontent d'un cran, sous le parent du dossier supprime.
   * @returns nombre d'entrees remises hors dossier et de sous-dossiers rattaches
   */
  async deleteFolder(id: string): Promise<{ readonly entries: number; readonly subfolders: number }> {
    const folders = await this.#repository.listFolders();
    const found = folders.find((folder) => folder.id === id);
    if (found === undefined) throw new Error(`dossier introuvable: ${id}`);
    const decrypted = decryptVaultFolder({ folder: found, vaultKey: this.#vaultKey });
    const deleted = markVaultFolderDeleted(decrypted);
    // Un sous-dossier pointant sur un dossier supprime sortirait de l'arborescence : l'export CXF
    // (collections construites depuis la racine) et l'index des chemins d'import ne le verraient plus.
    // S'il tenait son drapeau « sur en voyage » du dossier supprime, il le garde : le mode voyage
    // ne doit pas retirer de l'appareil ce que l'utilisateur avait emporte.
    const children = folders
      .filter((folder) => folder.parentId === id && folder.deletedAt === null)
      .map((folder) => {
        const moved = moveVaultFolder(decryptVaultFolder({ folder, vaultKey: this.#vaultKey }), decrypted.parentId);
        return decrypted.secrets.travelSafe === true ? setVaultFolderTravelSafe(moved, true) : moved;
      });
    await this.#repository.putFolders(
      [deleted, ...children].map((folder) => encryptVaultFolder({ folder, vaultKey: this.#vaultKey })),
    );
    // Les entrees du dossier supprime remontent a la racine plutot que de pointer dans le vide.
    // `include` : une entree archivee du dossier ne doit pas rester rattachee a un dossier supprime.
    // `trash: 'include'` : une entree jetee du dossier doit pouvoir etre restauree ailleurs que dans le vide.
    const items = await this.listEntries({ folderId: id, archived: 'include', trash: 'include' });
    for (const item of items) await this.moveEntry(item.id, null);
    return { entries: items.length, subfolders: children.length };
  }

  // --- Mode voyage (R18) ------------------------------------------------------

  /**
   * Marque (ou demarque) un dossier « sur en voyage ». Le drapeau est dans les
   * secrets chiffres : le serveur ne voit qu'une revision qui avance.
   */
  async setFolderTravelSafe(id: string, travelSafe: boolean): Promise<VaultFolder> {
    const folders = await this.#repository.listFolders();
    const found = folders.find((folder) => folder.id === id);
    if (found === undefined) throw new Error(`dossier introuvable: ${id}`);
    const next = setVaultFolderTravelSafe(decryptVaultFolder({ folder: found, vaultKey: this.#vaultKey }), travelSafe);
    await this.#repository.putFolder(encryptVaultFolder({ folder: next, vaultKey: this.#vaultKey }));
    return next;
  }

  /**
   * RETIRE de cet appareil tout ce qui n'est pas dans un dossier « sur en
   * voyage ». Le chemin qui AGIT du mode voyage : c'est ici que la donnee
   * quitte le disque, pas dans `travel-mode.ts` qui ne fait que decider.
   *
   * `remote` est le lot que le serveur pretend detenir : il est REAUTHENTIFIE
   * ici sous la `vaultKey` avant de servir de preuve de sauvegarde -- un
   * serveur qui bluffe ne doit pas pouvoir declencher une purge. Rien n'est
   * retire qui ne soit detenu par le serveur a une revision au moins aussi
   * recente ; ce qui reste est compte dans `unsynced`.
   *
   * Aucun tombstone n'est ecrit : la donnee quitte l'APPAREIL, pas le coffre.
   *
   * ponytail: `attachmentMetadata` (liste du serveur, NON authentifiable -- un
   * blob de 10 Mo ne se retelecharge pas pour prouver une sauvegarde) est cru
   * sur parole, exactement comme `planRecordTransfer` le croit deja pour
   * decider quoi pousser. Passer les blobs complets dans `attachments` si un
   * jour la preuve compte plus que la bande passante.
   */
  async purgeForTravelMode(remote: {
    readonly items?: readonly EncryptedVaultItem[];
    readonly folders?: readonly EncryptedVaultFolder[];
    readonly attachments?: readonly EncryptedVaultAttachment[];
    readonly attachmentMetadata?: readonly VaultAttachmentMetadata[];
  }): Promise<TravelPurgePlan> {
    const authentic = this.authenticateExternal(remote);
    const serverRevisions = new Map<string, number>();
    for (const record of [...authentic.items, ...authentic.folders, ...authentic.attachments, ...(remote.attachmentMetadata ?? [])]) {
      serverRevisions.set(record.id, Math.max(serverRevisions.get(record.id) ?? -1, record.revision));
    }
    const localFolders = await this.#repository.listFolders();
    const plan = planTravelPurge({
      items: (await this.#repository.listItems()).map((item) => ({
        id: item.id,
        revision: item.revision,
        folderId: item.folderId,
      })),
      folders: localFolders.map((folder) => ({
        id: folder.id,
        revision: folder.revision,
        parentId: folder.parentId,
        travelSafe: decryptVaultFolder({ folder, vaultKey: this.#vaultKey }).secrets.travelSafe === true,
      })),
      attachments: (await this.#repository.listAttachments()).map((attachment) => ({
        id: attachment.id,
        revision: attachment.revision,
        itemId: attachment.itemId,
        deletedAt: attachment.deletedAt,
      })),
      serverRevisions,
    });
    this.#attachmentSummaries.clear();
    await this.#repository.deleteRecords({
      items: plan.itemIds,
      folders: plan.folderIds,
      attachments: plan.attachmentIds,
    });
    return plan;
  }
}
